import type {FC} from 'react';
import {useState} from 'react';
import type {AssistantMemoryResponse} from 'src/api/ProactApiClient';
import {Alert} from 'src/design/atoms/Alert';
import {Badge} from 'src/design/atoms/Badge';
import {Button} from 'src/design/atoms/Button';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {DeleteActionIcon} from 'src/design/molecules/DeleteActionIcon';
import {EditActionIcon} from 'src/design/molecules/EditActionIcon';
import {SideDrawer} from 'src/design/organisms/SideDrawer';
import {
  useAcceptAssistantMemoryMutation,
  useCreateAssistantMemoryMutation,
  useDeleteAllAssistantMemoryMutation,
  useDeleteAssistantMemoryMutation,
  useGetAssistantMemoryQuery,
  useSetAssistantMemoryEnabledMutation,
  useUpdateAssistantMemoryMutation,
} from 'src/redux/queries/assistant';
import {formatUtcShortDate} from 'src/shared/utils/formatters';
import {
  isProposedMemory,
  isStaleMemory,
  memoryKindLabel,
  memorySourceLabel,
} from './assistantMemoryFormat';
import type {ScoutMemoryNoteValues} from './ScoutMemoryNoteForm';
import {ScoutMemoryNoteForm} from './ScoutMemoryNoteForm';
import {useMemoryRequest} from './useMemoryRequest';

export interface ScoutMemoryDrawerProps {
  opened: boolean;
  onClose: () => void;
}

type EditingTarget = number | 'new' | null;

const KindBadge: FC<{note: AssistantMemoryResponse}> = ({note}) => (
  <Badge size="sm" variant="light" color="gray">
    {memoryKindLabel(note.kind)}
  </Badge>
);

const noteMeta = (note: AssistantMemoryResponse): string => {
  const parts = [
    memorySourceLabel(note.source),
    `Created ${formatUtcShortDate(note.createdAt)}`,
    `Last used ${formatUtcShortDate(note.lastUsedAt, 'never')}`,
  ];
  if (isStaleMemory(note)) parts.push('Not used in 30 days');
  return parts.join(' - ');
};

/**
 * Side drawer where a user manages their Scout memory: the on/off switch, suggestions, notes with
 * inline add/edit, and Delete all.
 * @registry Scout memory management drawer (Use memory switch, suggestion and note cards, inline note form, Delete all).
 */
export const ScoutMemoryDrawer: FC<ScoutMemoryDrawerProps> = ({
  opened,
  onClose,
}) => {
  const run = useMemoryRequest();
  const {data, isLoading, isError} = useGetAssistantMemoryQuery(undefined, {
    skip: !opened,
  });
  const [setEnabled, {isLoading: isTogglingEnabled}] =
    useSetAssistantMemoryEnabledMutation();
  const [createMemory, {isLoading: isCreating}] =
    useCreateAssistantMemoryMutation();
  const [updateMemory, {isLoading: isUpdating}] =
    useUpdateAssistantMemoryMutation();
  const [deleteMemory] = useDeleteAssistantMemoryMutation();
  const [deleteAllMemory] = useDeleteAllAssistantMemoryMutation();
  const [acceptMemory] = useAcceptAssistantMemoryMutation();

  const [editing, setEditing] = useState<EditingTarget>(null);
  const [pendingDelete, setPendingDelete] =
    useState<AssistantMemoryResponse | null>(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

  const allNotes = data?.notes ?? [];
  const suggestions = allNotes.filter(isProposedMemory);
  const notes = allNotes.filter(note => !isProposedMemory(note));

  const createNote = async (request: ScoutMemoryNoteValues) => {
    if (await run(() => createMemory(request).unwrap())) setEditing(null);
  };

  const updateNote = async (id: number, request: ScoutMemoryNoteValues) => {
    if (await run(() => updateMemory({id, request}).unwrap())) setEditing(null);
  };

  const confirmDelete = async () => {
    const note = pendingDelete;
    setPendingDelete(null);
    if (!note) return;
    await run(() => deleteMemory(note.id).unwrap(), 'Memory note deleted.');
    if (editing === note.id) setEditing(null);
  };

  const confirmDeleteAllNotes = async () => {
    setConfirmDeleteAll(false);
    await run(() => deleteAllMemory().unwrap(), 'All memory notes deleted.');
    setEditing(null);
  };

  const renderBody = () => {
    if (isError)
      return (
        <Alert color="red" title="Unable to load memory">
          Your memory notes could not be loaded. Please try again.
        </Alert>
      );
    if (!data) return null;
    return (
      <Stack gap="md">
        <Switch
          label="Use memory"
          description="Scout reads these notes in new replies. Conversations started without memory ignore them."
          checked={data.enabled}
          disabled={isTogglingEnabled}
          onChange={event => {
            const enabled = event.currentTarget.checked;
            void run(() => setEnabled(enabled).unwrap());
          }}
        />
        {editing === 'new' ? (
          <ScoutMemoryNoteForm
            saving={isCreating}
            onSubmit={values => void createNote(values)}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <Group>
            <Button size="compact-sm" onClick={() => setEditing('new')}>
              Add a note
            </Button>
          </Group>
        )}
        {suggestions.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Suggestions
            </Text>
            {suggestions.map(note => (
              <Paper key={note.id} withBorder p="sm">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={4} style={{minWidth: 0}}>
                    <Group gap="xs">
                      <KindBadge note={note} />
                    </Group>
                    <Text size="sm" style={{overflowWrap: 'anywhere'}}>
                      {note.text}
                    </Text>
                  </Stack>
                  <Group gap="xs" wrap="nowrap">
                    <Button
                      size="compact-xs"
                      onClick={() =>
                        void run(() => acceptMemory(note.id).unwrap(), 'Memory note saved.')
                      }>
                      Save
                    </Button>
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      onClick={() => void run(() => deleteMemory(note.id).unwrap())}>
                      No thanks
                    </Button>
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
        {notes.length === 0 ? (
          <Text size="sm" c="dimmed">
            No memory notes yet. Ask Scout to remember something, or add a note here.
          </Text>
        ) : (
          <Stack gap="xs">
            {notes.map(note =>
              editing === note.id ? (
                <ScoutMemoryNoteForm
                  key={note.id}
                  initialValues={{kind: note.kind, text: note.text}}
                  saving={isUpdating}
                  onSubmit={values => void updateNote(note.id, values)}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <Paper key={note.id} withBorder p="sm" data-testid="memory-note">
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Stack gap={4} style={{minWidth: 0}}>
                      <Group gap="xs">
                        <KindBadge note={note} />
                      </Group>
                      <Text size="sm" style={{overflowWrap: 'anywhere'}}>
                        {note.text}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {noteMeta(note)}
                      </Text>
                    </Stack>
                    <Group gap={4} wrap="nowrap">
                      <EditActionIcon
                        tooltipText="Edit note"
                        onClick={() => setEditing(note.id)}
                      />
                      <DeleteActionIcon
                        tooltipText="Delete note"
                        onClick={() => setPendingDelete(note)}
                      />
                    </Group>
                  </Group>
                </Paper>
              )
            )}
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <>
      <SideDrawer opened={opened} onClose={onClose} size="480px">
        <SideDrawer.Header title="Scout memory" />
        <SideDrawer.Body loading={isLoading}>{renderBody()}</SideDrawer.Body>
        {allNotes.length > 0 && (
          <SideDrawer.Footer>
            <Button color="red" onClick={() => setConfirmDeleteAll(true)}>
              Delete all
            </Button>
          </SideDrawer.Footer>
        )}
      </SideDrawer>
      <ConfirmDialog
        opened={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Delete memory note"
        body={`Delete the note "${pendingDelete?.text ?? ''}"? Scout will stop using it.`}
        confirmationButtonColor="red"
        buttonTextYes="Delete"
        buttonTextNo="Cancel"
        size="sm"
      />
      <ConfirmDialog
        opened={confirmDeleteAll}
        onClose={() => setConfirmDeleteAll(false)}
        onConfirm={() => void confirmDeleteAllNotes()}
        title="Delete all memory notes"
        body={`Delete all ${allNotes.length} memory notes? This cannot be undone.`}
        confirmationButtonColor="red"
        buttonTextYes="Delete all"
        buttonTextNo="Cancel"
        size="sm"
      />
    </>
  );
};
